import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, effect, inject, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule, NativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { CookieService } from 'ngx-cookie-service';
import { MapperRegisterMemberCardStaffService } from '../../../../core/mappers/cityparking/mapper-register-member-card-staff.service';
import {
  RegisterMemberCardStaffResult,
  StaffFeeOption,
  StaffLineUserOption,
  StaffProvinceOption,
  StaffVehicleFormRow,
  StaffVehicleTypeOption,
} from '../../../../core/models/cityparking/register-member-card-staff.model';
import { ApiRegisterMemberCardStaffService } from '../../../../core/services/api/cityparking/api-register-member-card-staff.service';
import { ApiFormService } from '../../../../core/services/api-form.service';
import { ApiService } from '../../../../core/services/api.service';
import { ApiLineOaService } from '../../../../core/services/api/line-oa/api-line-oa.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { toLocalDateInput } from '../../../../core/utils/date-input.util';
import { PssConfirmModalComponent } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';

export const CUSTOM_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' } as Intl.DateTimeFormatOptions,
    monthYearLabel: { year: 'numeric', month: 'short' } as Intl.DateTimeFormatOptions,
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' } as Intl.DateTimeFormatOptions,
    monthYearA11yLabel: { year: 'numeric', month: 'long' } as Intl.DateTimeFormatOptions,
  },
};

@Component({
  selector: 'app-register-member-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    PssLoadingComponent,
    PssConfirmModalComponent,
  ],
  providers: [
    { provide: DateAdapter, useClass: NativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
  ],
  templateUrl: './register-member-card.component.html',
  styleUrl: './register-member-card.component.css',
})
export class RegisterMemberCardComponent implements OnInit, OnDestroy {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiRegisterMemberCardStaffService);
  private readonly mapper = inject(MapperRegisterMemberCardStaffService);
  private readonly apiForm = inject(ApiFormService);
  private readonly apiService = inject(ApiService);
  private readonly apiLineOa = inject(ApiLineOaService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  isLoading = false;
  isSearchingLine = false;
  showConfirm = false;
  copied = false;
  linkLine = true;
  name = '';
  phone = '';
  remark = '';
  discountCode = '';
  memberStart: Date = new Date();
  vehicleTypeId = 0;
  selectedFee: StaffFeeOption | null = null;
  selectedLine: StaffLineUserOption | null = null;
  vehicles: StaffVehicleFormRow[] = [this.createEmptyVehicle()];

  searchVehicleTypeText = '';
  searchFeeText = '';
  searchLineText = '';
  openDropdown: string | null = null;
  vehicleTypes: StaffVehicleTypeOption[] = [];
  provinces: StaffProvinceOption[] = [];
  fees: StaffFeeOption[] = [];
  lineOptions: StaffLineUserOption[] = [];
  result: RegisterMemberCardStaffResult | null = null;

  private lineSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    effect(() => {
      const siteId = this.siteState.selectedSite()?.id;
      untracked(() => {
        if (siteId) {
          this.onSiteChanged();
        }
      });
    });
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get filteredVehicleTypes(): StaffVehicleTypeOption[] {
    const term = this.searchVehicleTypeText.trim().toLowerCase();
    if (!term || this.selectedVehicleTypeLabel.toLowerCase() === term) {
      return this.vehicleTypes;
    }
    return this.vehicleTypes.filter((item) => item.typeName.toLowerCase().includes(term));
  }

  get filteredFees(): StaffFeeOption[] {
    const term = this.searchFeeText.trim().toLowerCase();
    if (!term || this.selectedFeeLabel.toLowerCase() === term) {
      return this.fees;
    }
    return this.fees.filter((item) => item.feeName.toLowerCase().includes(term));
  }

  get selectedVehicleTypeLabel(): string {
    return this.vehicleTypes.find((item) => item.id === this.vehicleTypeId)?.typeName ?? '';
  }

  get selectedFeeLabel(): string {
    if (!this.selectedFee) return '';
    return `${this.selectedFee.feeName} (${this.selectedFee.amount.toLocaleString()} บาท)`;
  }

  get qrUrl(): string {
    return this.result?.paymentUrl ? this.mapper.qrImageUrl(this.result.paymentUrl) : '';
  }

  filteredProvinces(row: StaffVehicleFormRow): StaffProvinceOption[] {
    const term = row.searchProvinceText.trim().toLowerCase();
    const selectedName = this.provinces.find((p) => p.id === row.provinceId)?.name ?? '';
    if (!term || selectedName.toLowerCase() === term) {
      return this.provinces;
    }
    return this.provinces.filter((item) => item.name.toLowerCase().includes(term));
  }

  ngOnInit(): void {
    this.loadVehicleTypes();
    this.loadProvinces();
  }

  ngOnDestroy(): void {
    if (this.lineSearchTimer) clearTimeout(this.lineSearchTimer);
    if (this.closeTimer) clearTimeout(this.closeTimer);
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
  }

  onInputFocus(field: string): void {
    this.openDropdown = field;
  }

  closeDropdowns(): void {
    if (this.closeTimer) clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      this.openDropdown = null;
      this.revertTypedDropdowns();
    }, 150);
  }

  selectVehicleType(item: StaffVehicleTypeOption): void {
    this.vehicleTypeId = item.id;
    this.searchVehicleTypeText = item.typeName;
    this.openDropdown = null;
    this.selectedFee = null;
    this.searchFeeText = '';
    this.loadFees();
  }

  selectFee(item: StaffFeeOption): void {
    this.selectedFee = item;
    this.searchFeeText = this.selectedFeeLabel;
    this.openDropdown = null;
  }

  selectLineUser(item: StaffLineUserOption): void {
    this.selectedLine = item;
    this.searchLineText = item.fullName || item.name;
    this.name = item.fullName || item.name;
    this.phone = item.phone;
    this.openDropdown = null;
  }

  selectProvince(index: number, item: StaffProvinceOption): void {
    const row = this.vehicles[index];
    if (!row) return;
    row.provinceId = item.id;
    row.province = item.name;
    row.searchProvinceText = item.name;
    this.openDropdown = null;
  }

  onProvinceFocus(index: number): void {
    this.openDropdown = `province-${index}`;
  }

  onLinkLineChange(value: boolean): void {
    this.linkLine = value;
    this.selectedLine = null;
    this.searchLineText = '';
    this.lineOptions = [];
    if (!value) {
      return;
    }
  }

  onLineSearchChange(): void {
    if (this.lineSearchTimer) clearTimeout(this.lineSearchTimer);
    this.lineSearchTimer = setTimeout(() => this.searchLineUsers(), 300);
  }

  addVehicle(): void {
    this.vehicles.push(this.createEmptyVehicle());
  }

  removeVehicle(index: number): void {
    if (this.vehicles.length === 1) {
      this.vehicles[0] = this.createEmptyVehicle();
      return;
    }
    this.vehicles.splice(index, 1);
  }

  askSubmit(): void {
    const error = this.validateForm();
    if (error) {
      this.toast.error(error);
      return;
    }
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    this.showConfirm = true;
  }

  closeConfirm(): void {
    this.showConfirm = false;
  }

  confirmSubmit(): void {
    this.showConfirm = false;
    this.submit();
  }

  openPaymentUrl(): void {
    if (!this.result?.paymentUrl) return;
    window.open(this.result.paymentUrl, '_blank');
  }

  copyPaymentUrl(): void {
    if (!this.result?.paymentUrl) return;
    navigator.clipboard.writeText(this.result.paymentUrl).then(
      () => {
        this.copied = true;
        this.toast.success('คัดลอกลิงก์แล้ว');
        if (this.copiedTimer) clearTimeout(this.copiedTimer);
        this.copiedTimer = setTimeout(() => {
          this.copied = false;
        }, 2000);
      },
      () => this.toast.error('คัดลอกไม่สำเร็จ')
    );
  }

  resetForm(): void {
    this.result = null;
    this.copied = false;
    this.name = '';
    this.phone = '';
    this.remark = '';
    this.discountCode = '';
    this.memberStart = new Date();
    this.selectedLine = null;
    this.searchLineText = '';
    this.lineOptions = [];
    this.vehicles = [this.createEmptyVehicle()];
  }

  private createEmptyVehicle(): StaffVehicleFormRow {
    return {
      license: '',
      provinceId: 0,
      province: '',
      searchProvinceText: '',
    };
  }

  private validateForm(): string | null {
    if (!this.selectedSiteId) return 'กรุณาเลือกลานจากแถบด้านบน';
    if (this.linkLine && !this.selectedLine) return 'กรุณาเลือกผู้ใช้ LINE OA';
    if (!this.name.trim()) return 'กรุณาระบุชื่อลูกค้า';
    if (!this.phone.trim()) return 'กรุณาระบุเบอร์โทร';
    if (!this.vehicleTypeId) return 'กรุณาเลือกประเภทรถ';
    if (!this.selectedFee) return 'กรุณาเลือกแพ็คเกจสมาชิก';
    if (!this.memberStart) return 'กรุณาเลือกวันเริ่มสมาชิก';
    const filled = this.vehicles.filter((v) => v.license.trim() || v.provinceId > 0);
    if (filled.length === 0) return 'กรุณาระบุทะเบียนรถอย่างน้อย 1 คัน';
    for (const vehicle of filled) {
      if (!vehicle.license.trim()) return 'กรุณาระบุหมายเลขทะเบียนรถ';
      if (vehicle.provinceId <= 0 || !vehicle.province.trim()) {
        return `กรุณาเลือกจังหวัดของทะเบียน ${vehicle.license.trim() || ''}`.trim();
      }
    }
    return null;
  }

  private submit(): void {
    const userId = Number(this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (userId <= 0 || menuId <= 0) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู — ตั้ง path_id 93 ในเมนู DB');
      return;
    }

    const vehicleTypeId = String(this.vehicleTypeId);
    this.isLoading = true;
    this.api
      .registerMemberCardByStaff({
        userId,
        menuId,
        siteId: this.selectedSiteId,
        feeId: this.selectedFee?.feeId ?? 0,
        subscriptionId: this.selectedFee?.id ?? 0,
        registerMemberType: 1,
        memberStart: toLocalDateInput(this.memberStart),
        name: this.name.trim(),
        phone: this.phone.trim(),
        remark: this.remark.trim(),
        discountCode: this.discountCode.trim(),
        linkLine: this.linkLine,
        cpUserId: this.linkLine ? this.selectedLine?.cpUserId ?? null : null,
        memberVehicles: this.vehicles
          .filter((v) => v.license.trim() && v.provinceId > 0)
          .map((v) => ({
            license: v.license.trim(),
            province: v.province.trim(),
            vehicleTypeId,
          })),
      })
      .subscribe({
        next: (dto) => {
          this.isLoading = false;
          const mapped = this.mapper.mapResult(dto);
          if (!mapped.isSuccess) {
            this.toast.error(mapped.message || 'สมัครไม่สำเร็จ');
            return;
          }
          this.result = mapped;
          this.toast.success(mapped.message || 'สมัครสำเร็จ');
        },
        error: (err) => {
          this.isLoading = false;
          this.toast.error(err?.error?.message || err?.error || 'สมัครไม่สำเร็จ');
        },
      });
  }

  private onSiteChanged(): void {
    this.selectedFee = null;
    this.searchFeeText = '';
    this.fees = [];
    this.result = null;
    if (this.vehicleTypeId) {
      this.loadFees();
    }
  }

  private loadVehicleTypes(): void {
    this.isLoading = true;
    this.apiForm.getParkingVehicleType().subscribe({
      next: (rows) => {
        this.vehicleTypes = this.mapper.mapVehicleTypes(rows ?? []);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.toast.error('โหลดประเภทรถไม่สำเร็จ');
      },
    });
  }

  private loadProvinces(): void {
    this.apiService.getVehicleMaster().subscribe({
      next: (res) => {
        this.provinces = this.mapper.mapProvinces(res?.cityParkingProvinceListResponses ?? []);
        if (this.provinces.length === 0) {
          this.toast.error('ไม่พบข้อมูลจังหวัด');
        }
      },
      error: () => {
        this.toast.error('โหลดจังหวัดไม่สำเร็จ');
      },
    });
  }

  private loadFees(): void {
    if (!this.selectedSiteId || !this.vehicleTypeId) {
      this.fees = [];
      return;
    }
    this.isLoading = true;
    this.apiService.getParkingFeeForMemberApi(this.selectedSiteId, this.vehicleTypeId).subscribe({
      next: (rows) => {
        this.fees = this.mapper.mapFees(rows ?? []);
        this.isLoading = false;
        if (this.fees.length === 0) {
          this.toast.error('ไม่พบแพ็คเกจในลานนี้');
        }
      },
      error: () => {
        this.isLoading = false;
        this.fees = [];
        this.toast.error('โหลดแพ็คเกจไม่สำเร็จ');
      },
    });
  }

  private searchLineUsers(): void {
    const text = this.searchLineText.trim();
    if (!text) {
      this.lineOptions = [];
      return;
    }
    const searchField = /^\d+$/.test(text) ? 'phone' : 'name';
    this.isSearchingLine = true;
    this.apiLineOa
      .getUserLineOAForTable({
        page: 1,
        pageSize: 20,
        searchField,
        searchText: text,
      })
      .subscribe({
        next: (res) => {
          this.lineOptions = this.mapper.mapLineUsers(res.data ?? []);
          this.isSearchingLine = false;
          this.openDropdown = 'line';
        },
        error: () => {
          this.isSearchingLine = false;
          this.toast.error('ค้นหา LINE OA ไม่สำเร็จ');
        },
      });
  }

  private revertTypedDropdowns(): void {
    this.searchVehicleTypeText = this.selectedVehicleTypeLabel;
    this.searchFeeText = this.selectedFeeLabel;
    if (this.selectedLine) {
      this.searchLineText = this.selectedLine.fullName || this.selectedLine.name;
    }
    for (const row of this.vehicles) {
      const selectedName = this.provinces.find((p) => p.id === row.provinceId)?.name ?? '';
      row.searchProvinceText = selectedName;
      if (!selectedName) {
        row.provinceId = 0;
        row.province = '';
      }
    }
  }
}
